import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiShoppingCart, FiMinus, FiPlus, FiArrowLeft } from 'react-icons/fi';
import { getRestaurant, getRestaurantMenu } from '../services/menuService';
import styles from './MenuPage.module.css';
import Logo from '../components/Logo';

export default function MenuPage() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract table parameter from URL query string
  const tableNumber = new URLSearchParams(location.search).get('table');

  useEffect(() => {
    const loadMenu = async () => {
      try {
        console.log('Loading menu for restaurant:', restaurantId);
        setLoading(true);
        setError(null);

        // Load restaurant and menu data in parallel
        const [restaurantData, menuData] = await Promise.all([
          getRestaurant(restaurantId),
          getRestaurantMenu(restaurantId)
        ]);
        
        console.log('Restaurant data:', restaurantData);
        console.log('Menu data:', menuData);

        if (!restaurantData) {
          throw new Error('Restaurant not found');
        }

        setRestaurant(restaurantData);
        setCategories(menuData);
        
        // Set first category as active if available
        if (menuData && menuData.length > 0) {
          setActiveCategory(menuData[0].id);
        }

        // Load cart from localStorage
        const savedCart = localStorage.getItem(`cart-${restaurantId}`);
        if (savedCart) {
          setCart(JSON.parse(savedCart));
        }
        
        // Handle table number - if no table number in URL, clear any stored table number
        if (!tableNumber) {
          localStorage.removeItem(`table-${restaurantId}`);
        }
      } catch (err) {
        console.error('Error loading menu:', err);
        setError(err.message || 'Failed to load menu');
      } finally {
        setLoading(false);
      }
    };

    loadMenu();
  }, [restaurantId, location.search, tableNumber]);

  const addToCart = (item) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.id === item.id);
      let newCart;
      
      if (existingItem) {
        newCart = prevCart.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      } else {
        newCart = [...prevCart, { ...item, quantity: 1 }];
      }
      
      localStorage.setItem(`cart-${restaurantId}`, JSON.stringify(newCart));
      return newCart;
    });
  };

  const removeFromCart = (itemId) => {
    setCart(prevCart => {
      const newCart = prevCart.map(item =>
        item.id === itemId
          ? { ...item, quantity: Math.max(0, item.quantity - 1) }
          : item
      ).filter(item => item.quantity > 0);
      
      localStorage.setItem(`cart-${restaurantId}`, JSON.stringify(newCart));
      return newCart;
    });
  };

  const getItemQuantity = (itemId) => {
    const item = cart.find(item => item.id === itemId);
    return item ? item.quantity : 0;
  };

  const getTotalItems = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading menu...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <h3>Error Loading Menu</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className={styles.retryButton}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={styles.menuContainer}>
      {/* Blue header bar */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerRow}>
            <div className={styles.logoContainer}>
              {restaurant?.logo_url ? (
                <img 
                  src={restaurant.logo_url} 
                  alt={restaurant.name} 
                  className={styles.restaurantLogo} 
                />
              ) : (
                <Logo color="white" size="large" />
              )}
              <h1 className={styles.restaurantName}>{restaurant?.name}</h1>
            </div>
          </div>
          
          {tableNumber && (
            <div className={styles.tableInfo}>
              Table {tableNumber}
            </div>
          )}
        </div>
      </header>

      <div className={styles.menuContent}>
        <nav className={styles.categoriesNav}>
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`${styles.categoryButton} ${
                activeCategory === category.id ? styles.active : ''
              }`}
            >
              {category.name}
            </button>
          ))}
        </nav>

        {categories.map(category => (
          <div
            key={category.id}
            className={styles.menuSection}
            style={{
              display: activeCategory === category.id ? 'block' : 'none'
            }}
          >
            <h2 className={styles.categoryTitle}>{category.name}</h2>
            
            {category.menu_items?.length === 0 ? (
              <p className={styles.noItems}>No items in this category</p>
            ) : (
              category.menu_items?.map(item => (
                <div key={item.id} className={styles.menuItem}>
                  <div className={styles.itemContent}>
                    {item.image_url && (
                      <div className={styles.menuItemImageContainer}>
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className={styles.menuItemImage} 
                        />
                      </div>
                    )}
                    <div className={styles.itemInfo}>
                      <h3 className={styles.itemName}>{item.name}</h3>
                      <p className={styles.itemDescription}>{item.description}</p>
                      <div className={styles.itemPrice}>${item.price.toFixed(2)}</div>
                    </div>
                  </div>
                  
                  <div className={styles.itemActions}>
                    {getItemQuantity(item.id) > 0 ? (
                      <div className={styles.quantityControl}>
                        <button
                          className={styles.quantityButton}
                          onClick={() => removeFromCart(item.id)}
                        >
                          <FiMinus />
                        </button>
                        <span className={styles.quantity}>
                          {getItemQuantity(item.id)}
                        </span>
                        <button
                          className={styles.quantityButton}
                          onClick={() => addToCart(item)}
                        >
                          <FiPlus />
                        </button>
                      </div>
                    ) : (
                      <button
                        className={styles.addToCartButton}
                        onClick={() => addToCart(item)}
                      >
                        <FiPlus /> Add to Cart
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ))}
      </div>

      {cart.length > 0 && (
        <button
          className={styles.cartButton}
          onClick={() => navigate(`/cart/${restaurantId}`, { state: { tableNumber } })}
        >
          <FiShoppingCart />
          <span className={styles.cartCount}>{getTotalItems()}</span>
        </button>
      )}
    </div>
  );
}
